import React from 'react';
import { SectionCard } from '../../common/SectionCard';
import { EmptyState } from '../../common/EmptyState';
import { SeverityPill } from '../../common/StatusPills';
import { Progress } from '../../ui/Progress';
import { formatAed, formatDate } from '../../../utils/format';
import { cn } from '../../../utils/cn';
import type { PlanBuildDetail } from '../../../types/domain';
const workstreamClasses = {
  'on-track': 'text-uae-leaf',
  'at-risk': 'text-[#8a6f11]',
  delayed: 'text-uae-red'
};
const milestoneClasses = {
  done: 'border-uae-leaf/30 bg-uae-leaf/10 text-uae-leaf',
  due: 'border-border bg-muted text-foreground',
  overdue: 'border-uae-red/30 bg-uae-red/10 text-uae-red'
};
export function PlanBuildPanel({
  planBuild
}: {
  planBuild?: PlanBuildDetail;
}) {
  if (!planBuild) {
    return <EmptyState title="Delivery baseline not established" description="Workstreams, schedule and budget draw-down appear once the record clears the Approval gate and the baseline is set." data-id="element-671" data-mp="197dhk823kga1" />;
  }
  const drawnPercent = Math.round(planBuild.budgetDrawnAed / planBuild.budgetMandateAed * 100);
  const meanComplete = Math.round(planBuild.workstreams.reduce((sum, ws) => sum + ws.complete, 0) / planBuild.workstreams.length);
  return <div className="space-y-4" data-id="element-672" data-mp="31d3w1e9crjk8">
      <div className="grid gap-4 lg:grid-cols-3" data-id="element-673" data-mp="31e5i0drwpqgq">
        <SectionCard title="Budget draw-down" data-id="element-674" data-mp="36cgpihuqbovs">
          <p className="font-mono text-xl font-semibold" data-id="element-675" data-mp="36bot54tb5n4g">
            {formatAed(planBuild.budgetDrawnAed)}
          </p>
          <p className="mt-1 text-[12px] text-muted-foreground" data-id="element-676" data-mp="3ac8t0g59n4ua">
            {drawnPercent}% of {formatAed(planBuild.budgetMandateAed)} mandate
          </p>
          <div className="mt-2" data-id="element-677" data-mp="3kchti66ajcfh">
            <Progress value={drawnPercent} data-id="element-678" data-mp="3kbgfptxw4bu2" />
          </div>
        </SectionCard>
        <SectionCard title="Schedule completion" data-id="element-679" data-mp="1l0fasy5hqm2v">
          <p className="font-mono text-xl font-semibold" data-id="element-680" data-mp="1py73vvhvge8n">{meanComplete}%</p>
          <p className="mt-1 text-[12px] text-muted-foreground" data-id="element-681" data-mp="1q0b3i18qmr34">
            Mean completion across {planBuild.workstreams.length} workstreams
          </p>
          <div className="mt-2" data-id="element-682" data-mp="1dwp66ghnevao">
            <Progress value={meanComplete} data-id="element-683" data-mp="1dxqjysq1tvw3" />
          </div>
        </SectionCard>
        <SectionCard title="Baseline" data-id="element-684" data-mp="1rwhvq8hfv5h2">
          <p className="text-xl font-semibold" data-id="element-685" data-mp="1rtc9d1072hp1">
            {planBuild.baselineApproved ? 'Approved' : 'Not approved'}
          </p>
          <p className="mt-1 text-[12px] text-muted-foreground" data-id="element-686" data-mp="20vlgri061uy7">
            {drawnPercent > meanComplete + 8 ? 'Draw-down is running ahead of completion — cost-to-complete review recommended.' : 'Draw-down is tracking within tolerance of completion.'}
          </p>
        </SectionCard>
      </div>

      <SectionCard title="Workstreams" description="Owners and completion against the approved baseline" bodyClassName="p-0" data-id="element-687" data-mp="25nn0tq3kspys">
        <ul className="divide-y divide-border" data-id="element-688" data-mp="2flj6i2dquspi">
          {planBuild.workstreams.map(workstream => <li key={workstream.id} className="px-4 py-3" data-id="element-689" data-mp="2z6jjqq1ew0je">
              <div className="flex items-center justify-between gap-3" data-id="element-690" data-mp="2z4ykin45jtf7">
                <div className="min-w-0" data-id="element-691" data-mp="3384p7qbm9cjk">
                  <p className="truncate text-[13px] font-medium" data-id="element-692" data-mp="2p8vkpnwrw6vf">{workstream.name}</p>
                  <p className="text-[11px] text-muted-foreground" data-id="element-693" data-mp="2pb9dtr7w790c">{workstream.owner}</p>
                </div>
                <div className="flex shrink-0 items-center gap-3" data-id="element-694" data-mp="2fj3q4toobdqy">
                  <span className={cn('text-[11px] capitalize', workstreamClasses[workstream.status])} data-id="element-695" data-mp="2fhsk0n7r9two">
                    {workstream.status.replace('-', ' ')}
                  </span>
                  <span className="font-mono text-[12px]" data-id="element-696" data-mp="2bi06rx7f2zkz">{workstream.complete}%</span>
                </div>
              </div>
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted" data-id="element-697" data-mp="1wmkbxpz0iznj">
                <div className={cn('h-full rounded-full', workstream.status === 'delayed' ? 'bg-uae-red' : workstream.status === 'at-risk' ? 'bg-uae-gold' : 'bg-uae-green')} style={{
              width: `${workstream.complete}%`
            }} data-id="element-698" data-mp="21lf0z9nkpkg3" />
              </div>
            </li>)}
        </ul>
      </SectionCard>

      <div className="grid gap-4 xl:grid-cols-2" data-id="element-699" data-mp="2l3waypk80hmr">
        <SectionCard title="Master schedule milestones" bodyClassName="p-0" data-id="element-700" data-mp="2h3krhg3lgncv">
          <ul className="divide-y divide-border" data-id="element-701" data-mp="2c513la5fx1hv">
            {planBuild.milestones.map(milestone => <li key={milestone.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-id="element-702" data-mp="2779d8kkg1nfs">
                <div className="min-w-0" data-id="element-703" data-mp="ac3hrya2z96f">
                  <p className="truncate text-[13px]" data-id="element-704" data-mp="5el988zz6vyy">{milestone.label}</p>
                  <p className="text-[11px] text-muted-foreground" data-id="element-705" data-mp="cym1qdkw6zxe">
                    Due {formatDate(milestone.due)}
                  </p>
                </div>
                <span className={cn('shrink-0 rounded-sm border px-1.5 py-0.5 text-[11px] capitalize', milestoneClasses[milestone.status])} data-id="element-706" data-mp="hw4debqant93">
                  {milestone.status}
                </span>
              </li>)}
          </ul>
        </SectionCard>

        <SectionCard title="Risk register" bodyClassName="p-0" data-id="element-707" data-mp="3v3fisg9djeur">
          <ul className="divide-y divide-border" data-id="element-708" data-mp="3v61zp6i5n6pn">
            {planBuild.risks.map(risk => <li key={risk.id} className="px-4 py-3" data-id="element-709" data-mp="40ad2h3l6c7l">
                <div className="flex items-start justify-between gap-3" data-id="element-710" data-mp="8xse7lbd3n51">
                  <p className="text-[13px] font-medium leading-snug" data-id="element-711" data-mp="1fdmcmfsbc8ny">{risk.label}</p>
                  <SeverityPill severity={risk.severity} data-id="element-712" data-mp="1kbxlan6vgd4l" />
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground" data-id="element-713" data-mp="1ke0k9wq05glg">Owner · {risk.owner}</p>
                <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground" data-id="element-714" data-mp="3gh5a57xgrotd">
                  {risk.mitigation}
                </p>
              </li>)}
          </ul>
        </SectionCard>
      </div>
    </div>;
}